import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  TopicList,
  CreateTopicListInput,
  UpdateTopicListInput,
  TopicPlatform,
} from '../model/contracts'
import { Button } from '../../../shared/ui/Button'

interface TopicListFormProps {
  topicList?: TopicList
  onSubmit: (data: CreateTopicListInput | UpdateTopicListInput) => void
  onCancel?: () => void
}

const PLATFORMS: TopicPlatform[] = ['all', 'facebook', 'x', 'twitter', 'threads']

export function TopicListForm({ topicList, onSubmit, onCancel }: TopicListFormProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(topicList?.name || '')
  const [platform, setPlatform] = useState<TopicPlatform>(topicList?.platform || 'all')
  const [keywordInput, setKeywordInput] = useState('')
  const [keywords, setKeywords] = useState<string[]>(topicList?.keywords || [])
  const [isActive, setIsActive] = useState(topicList?.isActive ?? true)

  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim()
    if (!trimmed) return

    // Split by comma, semicolon, or newline for bulk addition
    const newWords = trimmed
      .split(/[,;\n]+/)
      .map((w) => w.trim())
      .filter((w) => w && !keywords.includes(w))

    if (newWords.length > 0) {
      setKeywords([...keywords, ...newWords])
      setKeywordInput('')
    }
  }

  const handleRemoveKeyword = (keyword: string) => {
    setKeywords(keywords.filter((k) => k !== keyword))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddKeyword()
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    if (topicList) {
      const data: UpdateTopicListInput = {
        id: topicList.id,
        name,
        keywords,
        platform,
        isActive,
      }
      onSubmit(data)
    } else {
      const data: CreateTopicListInput = {
        name,
        keywords,
        platform,
      }
      onSubmit(data)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label
            htmlFor="topic-list-name"
            style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}
          >
            {t('topics.form.nameLabel')} *
          </label>
          <input
            id="topic-list-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              fontSize: 14,
            }}
            placeholder={t('topics.form.namePlaceholder')}
          />
        </div>

        <div>
          <label
            htmlFor="topic-list-platform"
            style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}
          >
            {t('topics.form.platformLabel')}
          </label>
          <select
            id="topic-list-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value as TopicPlatform)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              fontSize: 14,
            }}
          >
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {t(`platforms.${p}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label
          htmlFor="keyword-input"
          style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}
        >
          {t('topics.form.keywordsLabel')}
        </label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input
            id="keyword-input"
            type="text"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{
              flex: 1,
              padding: '10px 12px',
              borderRadius: 8,
              fontSize: 14,
            }}
            placeholder={t('topics.form.keywordPlaceholder')}
          />
          <Button type="button" onClick={handleAddKeyword}>
            {t('topics.actions.addKeyword')}
          </Button>
        </div>
        {keywords.length > 0 && (
          <div
            style={{
              padding: 12,
              borderRadius: 8,
              border: '1px solid var(--panel-border)',
              background: 'var(--surface-1)',
              minHeight: 60,
              maxHeight: 200,
              overflowY: 'auto',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            {keywords.map((keyword) => (
              <span
                key={keyword}
                className="bg-accent-tint"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  borderRadius: 6,
                  color: 'var(--accent)',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {keyword}
                <button
                  type="button"
                  onClick={() => handleRemoveKeyword(keyword)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--accent)',
                    cursor: 'pointer',
                    padding: 0,
                    margin: 0,
                    fontSize: 16,
                    lineHeight: 1,
                    opacity: 0.7,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {topicList && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            id="is-active"
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <label htmlFor="is-active" style={{ fontSize: 14 }}>
            {t('topics.status.active')}
          </label>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        {onCancel && (
          <Button type="button" variant="neutral" onClick={onCancel}>
            {t('sources.cancel')}
          </Button>
        )}
        <Button type="submit">
          {topicList ? t('topics.actions.updateList') : t('topics.actions.createList')}
        </Button>
      </div>
    </form>
  )
}
